/**
 * 🏠 Smart Dashboard Component - EMOTION-FIRST DESIGN
 * 
 * 🚨 PRODUCT RULES (NON-NEGOTIABLE):
 * 1. LifeFlo must NEVER feel like a water-first app
 * 2. Emotional support ALWAYS outranks health metrics
 * 3. Metrics are SUPPORTING DATA — never the opening interaction
 * 4. If emotional uncertainty exists, suppress health nudges
 * 5. Home screen must feel like "someone is here for me"
 * 
 * LAYOUT PRIORITY ORDER:
 * 1. Allora greeting (emotionally neutral or supportive)
 * 2. Soft emotional check-in (not every time)
 * 3. Chat entry point (visually dominant)
 * 4. Contextual suggestion (non-metric)
 * 5. Health metrics (hydration, steps) LAST and collapsed
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
  MessageCircle, Heart, ChevronRight, Sparkles, 
  ChevronDown, ChevronUp, Droplet, CheckSquare,
  Calendar, TrendingUp
} from 'lucide-react';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to sanitize text for display
const sanitizeText = (text) => {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

const SmartDashboard = () => {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMetrics, setShowMetrics] = useState(false); // Collapsed by default

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/home/smart-dashboard`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data.dashboard);
        
        // Auto-expand metrics only if NOT in emotional mode AND user has activity
        if (!data.dashboard?.emotional_mode && 
            !data.dashboard?.suppress_health_ui &&
            data.dashboard?.progress?.tasks_completed > 0) {
          setShowMetrics(true);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNudgeAction = (nudge) => {
    const actionType = nudge.action?.type;
    switch (actionType) {
      case 'open_chat':
        navigate('/chat');
        break;
      case 'log_water':
        navigate('/health');
        break;
      case 'view_tasks':
        navigate('/work');
        break;
      case 'pay_bill':
        navigate('/life?tab=bills');
        break;
      default:
        if (nudge.action?.url) {
          navigate(nudge.action.url);
        }
        break;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-32 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl"></div>
        <div className="h-24 bg-gray-50 rounded-2xl"></div>
      </div>
    );
  }

  const progress = dashboard?.progress || { water_glasses: 0, water_goal: 8, tasks_completed: 0, tasks_pending: 0 };
  // Note: nudges, emotionalCheckin, chatPrompt removed - handled in Home.js

  return (
    <div className="space-y-4">
      
      {/* ============================================
          HEALTH METRICS (Collapsed by default)
          NOTE: Emotional check-in and nudges removed to avoid
          duplication with Home.js "Talk to Allora" and "Daily Suggestion"
          ============================================ */}
        <div className="pt-2">
          <button
            onClick={() => setShowMetrics(!showMetrics)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <span className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {showMetrics ? t('home.hideTodaysProgress') || "Hide today's progress" : t('home.showTodaysProgress') || "Show today's progress"}
            </span>
            {showMetrics ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          
          {showMetrics && (
            <Card className="mt-2 bg-gray-50/80 border-gray-100 shadow-sm animate-fade-in">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  {/* Water Progress */}
                  <button 
                    onClick={() => navigate('/health')}
                    className="p-3 bg-white rounded-xl hover:bg-cyan-50 transition-all group border border-gray-100"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Droplet className="h-4 w-4 text-cyan-500" />
                      <span className="text-xs text-gray-500">
                        {progress?.water_glasses || 0}/{progress?.water_goal || 8}
                      </span>
                    </div>
                    <Progress 
                      value={Math.min((progress?.water_glasses / progress?.water_goal) * 100, 100)} 
                      className="h-1.5 bg-cyan-100" 
                    />
                    <p className="text-xs text-gray-500 mt-1.5">{t('home.hydration')}</p>
                  </button>

                  {/* Calories Progress */}
                  <button 
                    onClick={() => navigate('/health')}
                    className="p-3 bg-white rounded-xl hover:bg-orange-50 transition-all group border border-gray-100"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm">🔥</span>
                      <span className="text-xs text-gray-500">
                        {progress?.calories || 0}/{progress?.calories_goal || 2000}
                      </span>
                    </div>
                    <Progress 
                      value={Math.min(((progress?.calories || 0) / (progress?.calories_goal || 2000)) * 100, 100)} 
                      className="h-1.5 bg-orange-100" 
                    />
                    <p className="text-xs text-gray-500 mt-1.5">{t('home.calories')}</p>
                  </button>

                  {/* Tasks Progress */}
                  <button 
                    onClick={() => navigate('/work')}
                    className="p-3 bg-white rounded-xl hover:bg-blue-50 transition-all group border border-gray-100"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <CheckSquare className="h-4 w-4 text-blue-500" />
                      <span className="text-xs text-gray-500">
                        {progress?.tasks_completed || 0} {t('common.completed')}
                      </span>
                    </div>
                    <Progress 
                      value={progress?.tasks_pending > 0 
                        ? (progress.tasks_completed / (progress.tasks_completed + progress.tasks_pending)) * 100 
                        : 100
                      } 
                      className="h-1.5 bg-blue-100" 
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      {progress?.tasks_pending || 0} {t('common.pending')}
                    </p>
                  </button>

                  {/* Pending Bills - shows payment progress (inverted: fewer due = better) */}
                  <button 
                    onClick={() => navigate('/life')}
                    className="p-3 bg-white rounded-xl hover:bg-yellow-50 transition-all group border border-gray-100"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm">💳</span>
                      <span className={`text-xs font-medium ${progress?.bills_due > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {progress?.bills_due > 0 ? `${progress.bills_due} due` : '✓ All paid'}
                      </span>
                    </div>
                    <Progress 
                      value={progress?.bills_due > 0 ? Math.max(20, 100 - (progress.bills_due * 25)) : 100} 
                      className={`h-1.5 ${progress?.bills_due > 0 ? 'bg-amber-100' : 'bg-green-100'}`}
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Pending Bills
                    </p>
                  </button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

    </div>
  );
};

export default SmartDashboard;
